import { Box, Center, Spinner, VStack } from "@chakra-ui/react";
import { useDisclosure } from "@equidefi/hooks";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import type { DTO } from "@equidefi/shared/types";
import { BannerAlert, Heading, Text } from "@equidefi/ui";
import type React from "react";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";

import { useUserInvestments } from "@/hooks/useInvestments";
import { CompletedModal } from "@/pages/app/vault/CompletedModal";
import { OfferingFolder } from "./PortfolioDashboard/OfferingFolder";
import { VaultContainer } from "./components";

/**
 * PortfolioDashboard - New investor vault portfolio view (V2)
 *
 * Features:
 * - Groups investments by offering (Offering Folders)
 * - Each offering folder shows:
 *   - Issuer logo and name with custom brand color
 *   - Total amount invested in that offering
 *   - Number of investments in that offering
 * - Within each offering folder, investments are grouped by vault record
 * - Shows account type (Individual, Joint, Trust, Entity) for each vault group
 * - 560px max width for optimal mobile/tablet experience
 */
const PortfolioDashboard: React.FC = () => {
  useDocumentTitle("Portfolio");

  const { data: investments, isLoading, error, refetch } = useUserInvestments({}, { refetchOnMount: "always" });
  const navigate = useNavigate();
  const location = useLocation();
  const completedModal = useDisclosure();

  const handleModalClose = () => {
    refetch();
    completedModal.onClose();
    navigate(location.pathname, {
      replace: true,
      state: { refreshTimestamp: Date.now() },
    });
  };

  const completedInvestment = useMemo(() => {
    if (!location.state?.investment_id) return null;
    return investments?.find((investment: any) => investment.id === location?.state?.investment_id) ?? null;
  }, [investments, location?.state?.investment_id]);

  const { vaultId, accountType, isManualPayment, hasAdditionalSigners, expedited } = useMemo(() => {
    if (!completedInvestment) return { isManualPayment: false, hasAdditionalSigners: false, expedited: false };

    const manualPayment = completedInvestment.payments?.find(
      (payment) => payment.category === "MANUAL" && payment.status !== "canceled",
    );

    return {
      vaultId: completedInvestment.vault_id,
      accountType: completedInvestment.vault?.account_type,
      isManualPayment: !!manualPayment,
      hasAdditionalSigners: completedInvestment.signer_count > 1,
      expedited: !!completedInvestment.expedited,
    };
  }, [completedInvestment]);

  useEffect(() => {
    if (location?.state?.completed && completedInvestment) {
      completedModal.onOpen();
    }
  }, [location?.state?.completed, completedInvestment, completedModal.onOpen]);

  const investmentsByOffering = useMemo(() => {
    if (!investments) return new Map();

    const grouped = new Map<
      string,
      {
        offering: DTO.Offering;
        issuer: DTO.Issuer;
        investments: DTO.Investment[];
      }
    >();

    for (const investment of investments) {
      const offeringId = investment.offering_id;
      if (!offeringId) continue;

      if (!grouped.has(offeringId)) {
        grouped.set(offeringId, {
          offering: investment.offering,
          issuer: investment.issuer,
          investments: [],
        });
      }

      const group = grouped.get(offeringId);
      if (group) {
        group.investments.push(investment);
      }
    }

    return grouped;
  }, [investments]);

  if (isLoading) {
    return (
      <VaultContainer>
        <Center h="100vh">
          <Spinner />
        </Center>
      </VaultContainer>
    );
  }

  if (error) {
    return (
      <VaultContainer>
        <BannerAlert
          status="error"
          title="Failed to Load Investments"
          description="Please refresh the page or contact support if the problem persists."
        />
      </VaultContainer>
    );
  }

  return (
    <VaultContainer>
      <Box mb="4">
        <Heading fontWeight="bold" textStyle="h1" mb="1.5">
          Portfolio
        </Heading>
        <Text mb="0">Select an offering to view your investments.</Text>
      </Box>

      <VStack spacing="4" align="stretch">
        {Array.from(investmentsByOffering.values()).map(({ offering, issuer, investments }) => (
          <OfferingFolder
            key={offering.id}
            offering={offering}
            issuer={issuer}
            investments={investments}
            onOfferingClick={() => navigate(`/vault/offering/${offering.id}`)}
          />
        ))}

        {investmentsByOffering.size === 0 && (
          <Box textAlign="center" py="8">
            <Text color="gray.500">No investments found</Text>
          </Box>
        )}
      </VStack>

      <CompletedModal
        modal={{
          ...completedModal,
          onClose: handleModalClose,
        }}
        vaultId={vaultId}
        accountType={accountType}
        isManualPayment={isManualPayment}
        hasAdditionalSigners={hasAdditionalSigners}
        expedited={expedited}
      />
    </VaultContainer>
  );
};

export default PortfolioDashboard;
